<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('inventoryTableList.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('inventoryTableList.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-flex ts-justify-between">
          <div class="ts-flex ts-space-x-2">
            <div>
              <a-range-picker
                v-model="rangeDate"
                :allowClear="false"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                style="width:250px"
                @change="dateRangeChange"
              >
              </a-range-picker>
            </div>
            <ts-button
              outline
              v-tooltip="$t('inventoryTableList.excel')"
              :waiting="excel"
              @click.prevent="dowloadExcel"
              color="success"
            >
              <i class="fas fa-file-excel" v-if="!excel"></i>
            </ts-button>
          </div>
          <div class="d-flex ts-space-x-2">
            <a-select
              class="ts-w-full "
              v-model="branch_id"
              :options="branch_lists"
              style="width:200px"
              @change="onChangeBranch"
            ></a-select>
            <a-select
              class="ts-w-full "
              v-model="location_id"
              :options="location_lists"
              style="width:200px"
              @change="fetchData()"
            ></a-select>
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        :loading="loading"
        max-height="700"
        stripe
        highlight-row
        size="small"
      >
        <template slot-scope="{ row }" slot="product_name">
          <Tooltip
            :content="row.product_name"
            placement="top-start"
            max-width="500"
            theme="light"
            :delay="500"
          >
            <div class="ts-whitespace-no-wrap">
              {{ row.product_name ? row.product_name : '' }}
            </div>
          </Tooltip>
        </template>
        <template slot-scope="{ row }" slot="cost">
          <div class="ts-flex ts-justify-between">
            <span>$</span>
            <span>{{ currencyFormat(row.cost, 2) }}</span>
          </div>
        </template>
        <template slot-scope="{ row }" slot="sale_price">
          <div class="ts-flex ts-justify-between">
            <span>$</span>
            <span>{{ currencyFormat(row.sale_price, 2) }}</span>
          </div>
        </template>
        <template slot-scope="{ row }" slot="total_in">
          {{ totalIn(row) }}
        </template>
        <template slot-scope="{ row }" slot="total_out">
          {{ totalOut(row) }}
        </template>
        <template slot-scope="{ row }" slot="price_amount">
          <div class="ts-flex ts-justify-between">
            <span>$</span>
            <span>{{ currencyFormat(row.price_amount, 2) }}</span>
          </div>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { round } from 'lodash'

export default {
  name: 'inventoryList',
  data() {
    return {
      visible: false,
      loading: false,
      location_fetch: false,
      excel: false,
      branch_lists: [],
      location_lists: [],
      rangeDate: [
        moment()
          .startOf('M')
          .format('DD-MM-YYYY'),
        moment()
          .endOf('M')
          .format('DD-MM-YYYY')
      ]
    }
  },
  computed: {
    ...mapState('inventoryList', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.inventoryList.search
      },
      set(newValue) {
        this.$store.commit('inventoryList/SET_SEARCH', newValue)
        this.$store.commit('inventoryList/RESET_CURRENT_PAGE')
      }
    },
    branch_id: {
      get() {
        return this.$store.state.inventoryList.branch_id
      },
      set(newValue) {
        this.$store.commit('inventoryList/SET_BRANCH_ID', newValue)
      }
    },
    location_id: {
      get() {
        return this.$store.state.inventoryList.location_id
      },
      set(newValue) {
        this.$store.commit('inventoryList/SET_LOCATION_ID', newValue)
      }
    },
    columns() {
      return [
        {
          title: this.$t('inventoryTableList.barcode'),
          key: 'barcode',
          align: 'center',
          width: 80,
          minWidth: 80
        },
        {
          title: this.$t('inventoryTableList.description'),
          slot: 'product_name'
        },
        {
          title: this.$t('inventoryTableList.category'),
          key: 'category',
          width: 110,
          minWidth: 110
        },
        {
          title: this.$t('inventoryTableList.uom'),
          key: 'uom_name',
          align: 'center',
          width: 70,
          minWidth: 70
        },
        {
          title: this.$t('inventoryTableList.cost'),
          slot: 'cost',
          align: 'center',
          width: 85,
          minWidth: 85
        },
        {
          title: this.$t('inventoryTableList.price'),
          slot: 'sale_price',
          align: 'center',
          width: 85,
          minWidth: 85
        },

        {
          title: this.$t('inventoryTableList.begin'),
          key: 'begining_balance',
          align: 'center',
          width: 70,
          minWidth: 70
        },
        {
          title: this.$t('inventoryTableList.in'),
          align: 'center',
          children: [
            {
              title: this.$t('inventoryTableList.stock'),
              key: 'qty_in',
              align: 'center',
              width: 70,
              minWidth: 70
            },
            {
              title: this.$t('inventoryTableList.transfer'),
              key: 'transfer_in',
              align: 'center',
              width: 75,
              minWidth: 75
            },
            {
              title: this.$t('inventoryTableList.adjustment'),
              key: 'adjustment_in',
              align: 'center',
              width: 95,
              minWidth: 95
            },
            {
              title: this.$t('inventoryTableList.total'),
              slot: 'total_in',
              align: 'center',
              width: 70,
              minWidth: 70
            }
          ]
        },
        {
          title: this.$t('inventoryTableList.out'),
          align: 'center',
          children: [
            {
              title: this.$t('inventoryTableList.sale'),
              key: 'qty_out',
              align: 'center',
              width: 70,
              minWidth: 70
            },
            {
              title: this.$t('inventoryTableList.transfer'),
              key: 'transfer_out',
              align: 'center',
              width: 75,
              minWidth: 75
            },
            {
              title: this.$t('inventoryTableList.adjustment'),
              key: 'adjustment_out',
              align: 'center',
              width: 95,
              minWidth: 95
            },
            {
              title: this.$t('inventoryTableList.free'),
              key: 'qty_free',
              align: 'center',
              width: 72,
              minWidth: 72
            },
            {
              title: this.$t('inventoryTableList.total'),
              slot: 'total_out',
              align: 'center',
              width: 70,
              minWidth: 70
            }
          ]
        },
        {
          title: this.$t('inventoryTableList.ending'),
          key: 'ending_balance',
          align: 'center',
          width: 70,
          minWidth: 70
        },

        {
          title: this.$t('inventoryTableList.priceAmount'),
          slot: 'price_amount',
          align: 'center',
          width: 110,
          minWidth: 110
        }
      ]
    }
  },
  async mounted() {
    await this.fetchResource()
    this.fetchData()
  },
  methods: {
    currencyFormat(number, digit = 2) {
      return round(number, digit).toFixed(digit)
    },
    totalIn(row) {
      return round(
        parseFloat(row.qty_in) +
          parseFloat(row.transfer_in) +
          parseFloat(row.adjustment_in),
        0
      )
    },
    totalOut(row) {
      return round(
        parseFloat(row.qty_out) +
          parseFloat(row.transfer_out) +
          parseFloat(row.adjustment_out) +
          parseFloat(row.qty_free),
        0
      )
    },
    dateRangeChange(dateRange) {
      this.$store.commit('inventoryList/SET_DATE_FROM', dateRange[0])
      this.$store.commit('inventoryList/SET_DATE_TO', dateRange[1])
    },
    onChangeBranch() {
      this.location_id = null
      this.fetchLocation()
    },
    fetchData(attributes = {}) {
      this.loading = true
      this.$store
        .dispatch('inventoryList/fetch', attributes)
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchResource() {
      this.branch_id = this.$store.state.authUser.default_branch_id
      this.location_id = this.$store.state.authUser.cashier_location_id
      this.$store
        .dispatch('inventoryList/getBranch')
        .then(response => {
          this.branch_lists = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.fetchLocation()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    fetchLocation() {
      this.location_fetch = true
      this.$store
        .dispatch('inventoryList/getLocation', {
          branch_id: [this.branch_id]
        })
        .then(response => {
          this.location_fetch = false
          this.location_lists = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name
          }))
        })
        .catch(error => {
          this.location_fetch = false
          this.$toasted.error(error.message)
        })
    },
    dowloadExcel() {
      this.excel = true
      this.$store
        .dispatch('inventoryList/store')
        .then(response => {
          FileDownload(response, 'inventory_list.xlsx')
        })
        .catch(error => {
          this.location_fetch = false
          let parese_error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(error))
          )
          this.$toasted.error(parese_error.message)
        })
        .finally(() => {
          this.excel = false
        })
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500),
    rangeDate: debounce(function() {
      this.fetchData()
    }, 500),
  }
}
</script>
<style>
.ivu-table-cell {
  padding: 0 10px;
}
</style>
